import { Base64 } from 'js-base64';
import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { map, Observable } from 'rxjs';

import { TicketSource } from 'src/app/api/models/requests/ticket-source.enum';
import { TicketStateEnum } from 'src/app/api/models/requests/ticket-state.enum';
import { FilterCategory, FilterOption } from 'src/app/core/models/filtering.interface';
import { RequestsListPayload, RequestsSearchPayload } from '../models/requests.interface';
import { ForReviewFilter, RequestsFilteringGroups } from '../requests-filtering/requests-filtering.enum';
import { RequestItemDeep } from 'src/app/api/models/requests/requests.interface';
import { RequestsState, RequestsStore } from './requests.store';
import { RequestItem } from 'src/app/api/models/requests/requests.interface';
import { MineSort } from 'src/app/shared/mine-sort/mine-sort.interface';
import { RequestSortKey } from '../models/request-sort-key.enum';

@Injectable({
	providedIn: 'root'
})
export class RequestsQuery extends QueryEntity<RequestsState> {

	constructor(
		protected store: RequestsStore,
	) {
		super(store);
	}
	
	getSearchPayload(search: string, sort: MineSort, size?: number): RequestsSearchPayload {
		return { size, direction: sort.direction, term: Base64.encode(search) } as RequestsSearchPayload;
	}

	getListPayload(filters: FilterCategory[], sort?: MineSort, size?: number, term?: string): RequestsListPayload {
		const states: TicketStateEnum[] = [];
		const source: TicketSource[] = [];
		const country: string[] = [];
		const domain: string[] = [];
		const privacyRightIds: string[] = [];
		const assignedGroups: string[] = [];
		const brandIds: string[] = [];

		filters.find(f => f.id === RequestsFilteringGroups.Rights)?.options?.filter(o => o.selected)
		.forEach(o => privacyRightIds.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.Brand)?.options?.filter(o => o.selected)
			?.forEach(o => brandIds.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.Country)?.options?.filter(o => o.selected)
			.forEach(o => country.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.Domain)?.options?.filter(o => o.selected)
			.forEach(o => domain.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.Source)?.options?.filter(o => o.selected)
			.forEach(o => source.push(o.id as TicketSource));

		filters.find(f => f.id === RequestsFilteringGroups.Status)?.options?.filter(o => o.selected)
			.forEach(o => states.push(o.id as TicketStateEnum));

		filters.find(f => f.id === RequestsFilteringGroups.Assignee)?.options?.filter(o => o.selected)
			.forEach(o => assignedGroups.push(o.id));

		const stateOfResidence: string[] = [];
		const dataSubjectTypes: string[] = [];
		const dataSubjectTypeLabels: string[] = [];
		const closingReasons: string[] = [];

		filters.find(f => f.id === RequestsFilteringGroups.StateOfResidence)?.options?.filter(o => o.selected)
			.forEach(o => stateOfResidence.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.DataSubjectType)?.options?.filter(o => o.selected)
			.forEach(o => dataSubjectTypes.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.DataSubjectTypeLabel)?.options?.filter(o => o.selected)
			.forEach(o => dataSubjectTypeLabels.push(o.id));

		filters.find(f => f.id === RequestsFilteringGroups.ClosingReason)?.options?.filter(o => o.selected)
			.forEach(o => closingReasons.push(o.id));

		const minVerifiedAt = this.getMinDate(filters.find(f => f.id === RequestsFilteringGroups.MinVerifiedAt)?.options?.filter(o => o.selected));
		const minCreatedAt = this.getMinDate(filters.find(f => f.id === RequestsFilteringGroups.MinCreatedAt)?.options?.filter(o => o.selected));
		const duedate = this.getDueDateRange(filters.find(f => f.id === RequestsFilteringGroups.DueDate)?.options?.filter(o => o.selected));

		// both 3 together are "for review" category
		const errors = [];		
		const requireAttention = filters.find(f => f.id === RequestsFilteringGroups.ForReview)?.options?.filter(o => o.id === ForReviewFilter.RequireAttention && o.selected).length > 0;
		filters.find(f => f.id === RequestsFilteringGroups.ForReview)?.options?.filter(
			o => o.id === ForReviewFilter.AutopilotError && o.selected).forEach(o => errors.push(o.id));
		let readStatus = filters.find(f => f.id === RequestsFilteringGroups.ForReview)?.options?.filter(o => o.id === ForReviewFilter.Unread && o.selected).length > 0;
		const isNewRequest = filters.find(f => f.id === RequestsFilteringGroups.ForReview)?.options?.filter(o => o.id === ForReviewFilter.UnreadNewRequests && o.selected)?.length > 0;
		const hasNewResponse = filters.find(f => f.id === RequestsFilteringGroups.ForReview)?.options?.filter(o => o.id === ForReviewFilter.UnreadNewResponses && o.selected)?.length > 0;

		const payload = {
			size,
			source,
			domain,
			country,
			brandIds,
			privacyRightIds,
			state: states,
			minCreatedAt,
			assignedGroups,
			minDueDate: duedate?.minDueDate,
			maxDueDate: duedate?.maxDueDate,
			errors,
			unread: readStatus,
			requireAttention,
			isNewRequest,
			hasNewResponse,
			stateOfResidence,
			dataSubjectTypes,
			dataSubjectTypeLabels,
			minVerifiedAt,
			closingReasons,
		} as RequestsListPayload;

		if (sort) {
			payload['direction'] = sort.direction;
			payload['active'] = sort.active as RequestSortKey;
		}

		// Base64 like the dedicated search endpoint's `term` (see getSearchPayload)
		if (term) {
			payload.term = Base64.encode(term);
		}

		return payload;
	}

	private getDueDateRange(options: FilterOption[]): Partial<RequestsListPayload> {
		if (!options?.length) {
			return;
		}
		// Convert id from string to number (+) and 
		//find the highest value, representing the longest selected date range.
		const filterId = Math.max(...options.map(option => +option.id));
		const today = new Date();
		const yesterday = new Date();
		yesterday.setDate(today.getDate() - 1);
				
		const formatDate = (date: Date) => date.toISOString().split('T')[0];
		
		if (filterId === 0) {
			return { maxDueDate: formatDate(yesterday) };
		}
		
		const maxDueDate = new Date(today);
		maxDueDate.setDate(today.getDate() + (filterId + 1));
		
		// reaching this block means one of the selected options are bigger then 0 (not overdue) 
		// if there is mix of "overdue" and "due in X days" we need to return only the max due date
		const hasAlsoOverdue = options.some(option => option.id === '0');
		if (hasAlsoOverdue) {
			return { maxDueDate: formatDate(maxDueDate) };
		}

		// a range of due dates
		return {
			minDueDate: formatDate(yesterday),
			maxDueDate: formatDate(maxDueDate),
		};
	}

	private getMinDate(options: FilterOption[]): string {
		if (!options?.length) {
			return;
		}

		// Convert id from string to number (+) and 
		//find the highest value, representing the longest selected date range.
		const maxId = Math.max(...options.map(option => +option.id));

		// Calculate the date
		const date = new Date();
		date.setDate(date.getDate() - maxId);

		// Format the date as "YYYY-MM-dd" for BE
		const formattedDate = date.toISOString().split('T')[0];

		return formattedDate;
	}

	isOpen(state: TicketStateEnum): boolean {
		return (state === TicketStateEnum.Edit) || (state === TicketStateEnum.Processing) || (state === TicketStateEnum.Notify);
	}

	isClosed(state: TicketStateEnum): boolean {
		return (state === TicketStateEnum.Closed) || (state === TicketStateEnum.Redacted) || (state === TicketStateEnum.Redacting);
	}

	selectRequestByActionId(id: string): Observable<RequestItemDeep> {
		return this.selectAll({
			filterBy: [
				entity => entity.actionId === id,
				entity => entity.ticketSource === TicketSource.MineApp,
				entity => entity.ticketState !== TicketStateEnum.Redacted && entity.ticketState !== TicketStateEnum.Closed,
			],
		}).pipe(map(res => <RequestItemDeep>res[0]));
	}

	private getRequestState(request: RequestItemDeep | RequestItem): TicketStateEnum {
		return typeof request.ticketState === 'string' ? request.ticketState : request.ticketState?.state;
	}
}